import { basePathRewrite } from '@components/common';
import rmcCategoriesObj from '@components/common/rmcCategories.json';
import { RemoteCategoriesMetaTags } from '@components/metaComponent/remoteCategories';
import { IDeviceType } from '@components/shared.types';
import {
  CategoryListBlock,
  deviceTypeByUserAgent,
} from '@license-admin/boldjobs';
import type { GetServerSideProps } from 'next';
import Head from 'next/head';

interface Category {
  id: number;
  slug: string;
  name: string;
}

interface RawCategory {
  category: Category;
}

interface Props {
  categories: Category[];
  deviceType: IDeviceType;
  themeType?: string;
  schemaDateModified: string;
}

export default function CategoriesPage({
  categories,
  themeType,
  schemaDateModified,
}: Props) {
  const breadCrumbProps = {
    breadCrumbName: 'remote-job-categories',
    breadCrumbNameValue: [''],
    breadCrumbsData: [
      {
        page: 'remote-job-categories',
        title: 'Remote Job Categories | Work-from-Home Careers by Industry',
        children: [
          { name: 'Home', path: '/', static: false },
          { name: 'Remote Job Categories', static: true },
        ],
      },
    ],
  };

  return (
    <>
      <Head>
        {RemoteCategoriesMetaTags({
          categoryName: 'categoryList',
          schemaDateModified,
        })}
      </Head>

      <CategoryListBlock
        pageTitle="Remote Job Categories – Find the Best Work-from-Home Opportunities"
        categoryData={categories}
        breadCrumbProps={breadCrumbProps}
        basePathRewrite={basePathRewrite}
        minDefaultItems={12}
        themeType={themeType}
      />
    </>
  );
}

export const getServerSideProps: GetServerSideProps<Props> = async (
  context
) => {
  const { req } = context;

  const deviceType: IDeviceType = deviceTypeByUserAgent(
    req.headers['user-agent'] || ''
  );
  // Sort categories from imported JSON, including job type pages
  const categories: Category[] = [
    ...rmcCategoriesObj.map((item: RawCategory) => item.category),
    { id: 0, slug: 'full-time', name: 'Full-Time' },
    { id: 0, slug: 'part-time', name: 'Part-Time' },
    { id: 0, slug: 'freelance', name: 'Freelance' },
    { id: 0, slug: 'entry-level', name: 'Entry Level' },
    { id: 0, slug: 'temp', name: 'Temporary' },
  ].filter(
    (category, index, categoryList) =>
      categoryList.findIndex((item) => item.slug === category.slug) === index
  ).sort((a, b) => a.name.localeCompare(b.name));

  return {
    props: {
      categories,
      deviceType,
      themeType: 'graphite',
      schemaDateModified: new Date().toISOString(),
    },
  };
};
